import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { extractApiErrorMessage } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, Mail } from "lucide-react";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
      toast.success("تم إرسال رابط الاستعادة");
    } catch (err) {
      toast.error(extractApiErrorMessage(err, "تعذّر إرسال الطلب"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <section className="max-w-md mx-auto px-6 py-16" data-testid="forgot-password-page">
        <div className="card-warm p-8">
          {!sent ? (
            <>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F6F4EE] flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#0A4D3C]" />
                </div>
                <div>
                  <h1 className="font-heading text-3xl text-[#222222]">نسيت كلمة المرور؟</h1>
                </div>
              </div>
              <p className="text-[#5E5C58] text-sm mt-3 leading-relaxed">
                أدخل بريدك الإلكتروني المسجّل وسنرسل لكم رابطًا لإعادة تعيين كلمة المرور
                (الرابط صالح لمدة ٦٠ دقيقة).
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="email">البريد الإلكتروني</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-2"
                    dir="ltr"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white h-11"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? "جاري الإرسال..." : "إرسال رابط الاستعادة"}
                </Button>
              </form>
              <p className="text-sm text-[#5E5C58] mt-6 text-center">
                تذكرت كلمة المرور؟{" "}
                <Link
                  to="/login"
                  className="text-[#0A4D3C] underline"
                  data-testid="forgot-back-to-login"
                >
                  عودة لتسجيل الدخول
                </Link>
              </p>
            </>
          ) : (
            <div className="text-center" data-testid="forgot-success-state">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600" />
              <h1 className="font-heading text-3xl text-[#222222] mt-4">تم إرسال الرابط</h1>
              <p className="text-[#5E5C58] mt-3 leading-relaxed">
                إذا كان البريد <strong dir="ltr">{email}</strong> مُسجّلًا لدينا،
                فستصلك رسالة فيها رابط إعادة تعيين كلمة المرور خلال لحظات.
              </p>
              <p className="text-[#5E5C58] text-sm mt-3">
                تحقّق من مجلد الرسائل غير المرغوبة (Spam) في حال لم تصلكم الرسالة خلال دقيقتين.
              </p>
              <div className="mt-6 flex justify-center gap-3">
                <Link to="/login">
                  <Button
                    variant="outline"
                    className="rounded-full"
                    data-testid="forgot-success-back-login"
                  >
                    عودة لتسجيل الدخول
                  </Button>
                </Link>
                <a
                  href="https://wa.me/966538278484?text=السلام%20عليكم%2C%20أحتاج%20مساعدة%20بخصوص%20استعادة%20كلمة%20المرور."
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    className="rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white"
                    data-testid="forgot-success-whatsapp"
                  >
                    تواصل عبر الواتساب
                  </Button>
                </a>
              </div>
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
};

export default ForgotPassword;
